import React from "react";
import { getGroupBatchSelectLabel } from "@/features/contacts/utils/groupSelection";
import TextButton from "@/shared/components/ui/Button/TextButton";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  ArrowUp,
  ArrowDown,
  Check,
} from "lucide-react";
import Pagination from "@/shared/components/ui/Pagination/Pagination";

const SELECTION_COLUMN_WIDTH = "40px";
const PINNED_COLUMN_SHADOW = "shadow-[2px_1px_5px_-6px_rgba(15,23,42,0.45)]";

export default function GroupedContactTableSection({
  groups = [],
  expandedGroups = {},
  openGroups = new Set(),
  onToggleExpand,
  onLoadMore,
  columns = [],
  renderCell,
  totalCount = 0,
  rowsPerPage = 15,
  page = 1,
  onPageChange,
  selectedContacts = [],
  onSelectionChange,
  sortDescriptor = {},
  onSortChange,
  isEntireListSelected = false,
  selectedGroupIds = new Set(),
  onToggleGroupSelect,
  onSelectNextInGroup,
  batchSelectingGroupId = null,
  height,
}) {
  const isContactSelected = (id) =>
    (selectedContacts || []).some((c) => c?.id === id);

  const onToggleContactSelect = (contact) => {
    const isSel = isContactSelected(contact.id);
    if (isSel) {
      onSelectionChange(selectedContacts.filter((c) => c.id !== contact.id));
    } else {
      onSelectionChange([
        ...selectedContacts,
        {
          id: contact.id,
          name: contact.name,
          email: contact.email,
          company: contact.company,
        },
      ]);
    }
  };

  const handleGroupSelect = (e, group, isCurrentlyAllSelected) => {
    e.stopPropagation();
    onToggleGroupSelect?.(group.id, !isCurrentlyAllSelected);
  };

  const handleHeaderClick = (col) => {
    if (!col.sortable) return;
    let nextDirection = "ascending";
    if (sortDescriptor.column === col.uid) {
      if (sortDescriptor.direction === "ascending") {
        nextDirection = "descending";
      } else if (sortDescriptor.direction === "descending") {
        nextDirection = null;
      }
    }
    const nextCol = nextDirection ? col.uid : "";
    onSortChange({ column: nextCol, direction: nextDirection });
  };

  const showPagination = onPageChange && totalCount > rowsPerPage;

  const getColumnWidthStyle = (col) =>
    col.width
      ? { width: col.width, minWidth: col.width, maxWidth: col.width }
      : {};

  return (
    <div
      className="w-full flex-1 min-h-0 flex flex-col gap-4 relative overflow-hidden mt-2"
      style={height ? { height } : undefined}
    >
      <div className="overflow-hidden flex-1 min-h-0">
        <div
          className="overflow-auto h-full rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900"
          style={{ paddingBottom: showPagination ? "48px" : 0 }}
        >
          <table className="min-w-full border-separate border-spacing-0 table-fixed">
            <thead>
              <tr className="pb-2 sticky top-0 bg-gray-100 dark:bg-gray-900 z-[9999]">
                <th
                  className="pr-2 pl-6 border-b border-gray-100 dark:border-gray-800 sticky left-0 z-[10001] py-4 text-center bg-gray-100 dark:bg-gray-900"
                  style={{
                    width: SELECTION_COLUMN_WIDTH,
                    minWidth: SELECTION_COLUMN_WIDTH,
                    maxWidth: SELECTION_COLUMN_WIDTH,
                  }}
                />
                {columns.map((col, colIndex) => {
                  const isPinnedNameColumn = colIndex === 0;
                  const columnStyle = {
                    ...getColumnWidthStyle(col),
                    ...(isPinnedNameColumn
                      ? { left: SELECTION_COLUMN_WIDTH }
                      : {}),
                  };

                  return (
                    <th
                      key={col.uid}
                      className={`px-3 py-3 text-xs text-gray-800 dark:text-gray-200 font-semibold tracking-wider cursor-pointer ${
                        col.align === "center"
                          ? "text-center"
                          : col.align === "right"
                            ? "text-right"
                            : "text-left"
                      } ${
                        isPinnedNameColumn
                          ? `sticky z-[10000] bg-gray-100 dark:bg-gray-900 ${PINNED_COLUMN_SHADOW}`
                          : ""
                      } border-b border-gray-100 dark:border-gray-800`}
                      style={columnStyle}
                      onClick={() => handleHeaderClick(col)}
                    >
                      <div
                        className={`flex items-center font-semibold cursor-pointer w-full ${
                          col.align === "center"
                            ? "justify-center"
                            : col.align === "right"
                              ? "justify-end"
                              : "justify-start"
                        } ${
                          sortDescriptor.column === col.uid
                            ? "font-semibold text-gray-900 dark:text-gray-100"
                            : "font-normal hover:text-gray-900 dark:hover:text-gray-100"
                        }`}
                      >
                        {col.name}
                        {sortDescriptor.column === col.uid && (
                          <span>
                            {sortDescriptor.direction === "ascending" ? (
                              <ArrowUp size={10} strokeWidth={3} />
                            ) : (
                              <ArrowDown size={10} strokeWidth={3} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
              {(groups || []).map((group) => {
                const expanded = openGroups?.has?.(group.id) ?? false;
                const gState = expandedGroups?.[group.id] || {};
                const groupContacts = Array.isArray(gState.contacts) ? gState.contacts : [];
                const gAll =
                  isEntireListSelected ||
                  selectedGroupIds.has(group.id) ||
                  (groupContacts.length > 0 &&
                    groupContacts.every((c) => isContactSelected(c?.id)));
                const gSome =
                  !gAll &&
                  (selectedGroupIds.has(group.id) ||
                    groupContacts.some((c) => isContactSelected(c?.id)));
                const selectedInGroup = groupContacts.filter((c) => isContactSelected(c?.id)).length;
                const groupLabel = group.name || group.label || group.title || group.company_name || "(No account)";
                const batchSelectLabel = onSelectNextInGroup
                  ? getGroupBatchSelectLabel({
                      groupCount: group.count,
                      selectedInGroup,
                      isFullySelected: gAll,
                    })
                  : null;
                const isBatchSelecting = batchSelectingGroupId === group.id;

                return (
                  <React.Fragment key={group.id}>
                    {/* Group Header Row */}
                    <tr
                      className="group bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/60 cursor-pointer transition-colors duration-150"
                      onClick={() => onToggleExpand(group.id)}
                    >
                      <td
                        className="pl-6 pr-2 border-b border-gray-100 dark:border-gray-800 sticky left-0 z-20 bg-gray-50 dark:bg-gray-800/50 py-2 text-center group-hover:bg-gray-100 dark:group-hover:bg-gray-700/60 transition-colors duration-150"
                        style={{
                          width: SELECTION_COLUMN_WIDTH,
                          minWidth: SELECTION_COLUMN_WIDTH,
                          maxWidth: SELECTION_COLUMN_WIDTH,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex justify-center items-center gap-1">
                          <label className="inline-flex relative">
                            <input
                              type="checkbox"
                              className="absolute opacity-0 w-0 h-0"
                              checked={gAll}
                              onChange={(e) =>
                                handleGroupSelect(e, group, gAll)
                              }
                            />
                            <span
                              className={`w-4 h-4 rounded border ${
                                gAll
                                  ? "bg-secondary border-secondary"
                                  : gSome
                                    ? "bg-secondary/90 border-secondary"
                                    : "border-gray-400 dark:border-gray-600 bg-white dark:bg-gray-800"
                              } flex items-center justify-center cursor-pointer`}
                            >
                              {gSome && !gAll ? (
                                <span className="block w-2 h-0.5 bg-white rounded" />
                              ) : gAll ? (
                                <Check size={12} className="text-white stroke-[3.5]" />
                              ) : null}
                            </span>
                          </label>
                        </div>
                      </td>
                      {(columns || []).map((col, colIndex) => {
                        const isPinnedNameColumn = colIndex === 0;
                        if (isPinnedNameColumn) {
                          return (
                            <td
                              key={col.uid}
                              className={`px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-left sticky bg-gray-50 dark:bg-gray-800/50 z-20 group-hover:bg-gray-100 dark:group-hover:bg-gray-700/60 transition-colors duration-150 ${PINNED_COLUMN_SHADOW}`}
                              style={{
                                left: SELECTION_COLUMN_WIDTH,
                                ...getColumnWidthStyle(col),
                              }}
                            >
                              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                {expanded ? (
                                  <ChevronDown size={18} className="shrink-0 text-gray-600 dark:text-gray-400" />
                                ) : (
                                  <ChevronRight size={18} className="shrink-0 text-gray-600 dark:text-gray-400" />
                                )}
                                <span className="truncate">{groupLabel}</span>
                                <span className="text-xs font-normal text-gray-500 dark:text-gray-400 shrink-0">
                                  ({group.count})
                                </span>
                                {selectedInGroup > 0 && !gAll && (
                                  <StatusChip
                                    label={`${selectedInGroup} selected`}
                                    variant="lightBlue"
                                    className="shrink-0"
                                  />
                                )}
                                {batchSelectLabel && (
                                  <TextButton
                                    className="ml-1"
                                    tooltip={`Adds the next unselected contacts in ${groupLabel}, in the order shown, to your selection.`}
                                    isLoading={isBatchSelecting}
                                    loadingText="Selecting…"
                                    onClick={() => onSelectNextInGroup(group.id, groupLabel)}
                                  >
                                    {batchSelectLabel}
                                  </TextButton>
                                )}
                              </div>
                            </td>
                          );
                        }

                        return (
                          <td
                            key={col.uid}
                            className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 group-hover:bg-gray-100 dark:group-hover:bg-gray-700/60 transition-colors duration-150"
                            style={getColumnWidthStyle(col)}
                          />
                        );
                      })}
                    </tr>

                    {/* Group Contacts (when expanded) */}
                    {expanded &&
                      groupContacts.map((contact) => (
                        <tr
                          key={contact.id}
                          className="group hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900 transition-colors duration-150 cursor-pointer"
                        >
                          <td
                            className="pl-6 pr-2 border-b border-gray-100 dark:border-gray-800 sticky left-0 z-20 bg-white dark:bg-gray-900 py-[0.98rem] text-center group-hover:bg-gray-50 dark:group-hover:bg-gray-800 transition-colors duration-150"
                            style={{
                              width: SELECTION_COLUMN_WIDTH,
                              minWidth: SELECTION_COLUMN_WIDTH,
                              maxWidth: SELECTION_COLUMN_WIDTH,
                            }}
                          >
                            <div className="flex justify-center">
                              <label className="inline-flex relative">
                                <input
                                  type="checkbox"
                                  className="absolute opacity-0 w-0 h-0"
                                  checked={isContactSelected(contact.id)}
                                  onChange={() =>
                                    onToggleContactSelect(contact)
                                  }
                                />
                                <span
                                  className={`w-4 h-4 rounded border ${
                                    isContactSelected(contact.id)
                                      ? "bg-secondary border-secondary"
                                      : "border-gray-400 dark:border-gray-600 bg-white dark:bg-gray-800"
                                  } flex items-center justify-center cursor-pointer`}
                                >
                                  {isContactSelected(contact.id) && (
                                    <Check size={12} className="text-white stroke-[3.5]" />
                                  )}
                                </span>
                              </label>
                            </div>
                          </td>

                          {(columns || []).map((col, colIndex) => {
                            const isPinnedNameColumn = colIndex === 0;
                            const columnStyle = {
                              ...getColumnWidthStyle(col),
                              ...(isPinnedNameColumn
                                ? { left: SELECTION_COLUMN_WIDTH }
                                : {}),
                            };

                            return (
                              <td
                                key={col.uid}
                                className={`px-3 py-3 border-b border-gray-100 dark:border-gray-800 whitespace-nowrap overflow-y-auto text-sm text-gray-700 dark:text-gray-200 transition-colors duration-150 ${
                                  col.align === "center"
                                    ? "text-center"
                                    : col.align === "right"
                                      ? "text-right"
                                      : "text-left"
                                } ${
                                  isPinnedNameColumn
                                    ? `sticky z-10 bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800 ${PINNED_COLUMN_SHADOW}`
                                    : ""
                                }`}
                                style={columnStyle}
                              >
                                {renderCell(contact, col.uid)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}

                    {/* Group loading row */}
                    {expanded && gState.loading && (
                      <tr className="bg-white dark:bg-gray-900">
                        <td
                          colSpan={(columns?.length || 0) + 1}
                          className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 sticky left-0 bg-white dark:bg-gray-900 z-10"
                        >
                          <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-sm w-[calc(100vw-300px)] max-w-full py-1">
                            <Loader2
                              size={16}
                              className="animate-spin text-secondary"
                            />
                            <span>Loading...</span>
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Group Empty row */}
                    {expanded &&
                      !gState.loading &&
                      groupContacts.length === 0 && (
                        <tr className="bg-white dark:bg-gray-900">
                          <td
                            colSpan={(columns?.length || 0) + 1}
                            className="px-4 py-4 border-b border-gray-100 dark:border-gray-800 text-gray-500 dark:text-gray-400 text-xs font-medium sticky left-0 bg-white dark:bg-gray-900 z-10"
                          >
                            <div className="flex items-center justify-center w-[calc(100vw-300px)] max-w-full">
                              No contacts found.
                            </div>
                          </td>
                        </tr>
                      )}

                    {/* Show More Row */}
                    {expanded && !gState.loading && gState.hasMore && (
                      <tr className="bg-white dark:bg-gray-900 hover:bg-gray-50/50 dark:hover:bg-gray-800/50">
                        <td
                          className="pl-6 pr-2 border-b border-gray-100 dark:border-gray-800 sticky left-0 z-20 bg-white dark:bg-gray-900 py-2"
                          style={{
                            width: SELECTION_COLUMN_WIDTH,
                            minWidth: SELECTION_COLUMN_WIDTH,
                            maxWidth: SELECTION_COLUMN_WIDTH,
                          }}
                        />
                        {columns.map((col, colIndex) => {
                          const isPinnedNameColumn = colIndex === 0;
                          if (isPinnedNameColumn) {
                            return (
                              <td
                                key={col.uid}
                                className={`py-2.5 pr-4 border-b border-gray-100 dark:border-gray-800 text-left sticky bg-white dark:bg-gray-900 z-10 ${PINNED_COLUMN_SHADOW}`}
                                style={{
                                  left: SELECTION_COLUMN_WIDTH,
                                  ...getColumnWidthStyle(col),
                                }}
                              >
                                <button
                                  onClick={() => onLoadMore(group.id)}
                                  className="text-secondary text-xs font-semibold underline hover:text-secondary-hover bg-transparent border-none p-0 cursor-pointer transition-colors"
                                >
                                  Show More
                                </button>
                              </td>
                            );
                          }

                          return (
                            <td
                              key={col.uid}
                              className="border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900"
                              style={getColumnWidthStyle(col)}
                            />
                          );
                        })}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showPagination && (
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-20 flex justify-center bg-gradient-to-t from-white dark:from-gray-900 from-60% to-transparent pt-6">
          <div className="pointer-events-auto w-full flex justify-center">
            <Pagination
              currentPage={page}
              totalCount={totalCount}
              rowsPerPage={rowsPerPage}
              onPageChange={onPageChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
